import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { RichTextPreview } from '@/components/editor/rich-text-preview';
import { type FormField, formFieldPresentation } from '@/components/shared/form/form-fields';
import { CheckboxGroupInput } from '@/components/ui/form/checkbox-group-input';
import { TextInput } from '@/components/ui/form/text-input';
import { richTextToPlainText } from '@/lib/rich-text';

const OTHER_VALUE = '__other__';

function optionValueSet(field: FormField) {
  return new Set((field.options ?? []).map((option) => option.value));
}

function optionValueSignature(field: FormField) {
  return (field.options ?? []).map((option) => option.value).join('');
}

function equalStringArrays(first: string[], second: string[]) {
  return first.length === second.length && first.every((entry, index) => entry === second[index]);
}

function multipleChoiceParentValues(checkboxValues: string[], otherText: string) {
  const regularValues = checkboxValues.filter((entry) => entry !== OTHER_VALUE);
  if (checkboxValues.includes(OTHER_VALUE) && otherText) {
    return [...regularValues, otherText];
  }

  return regularValues;
}

function multipleChoiceState(field: FormField, value: string[]) {
  const regularOptionValues = optionValueSet(field);

  return {
    checkboxValues: value.map((entry) => (regularOptionValues.has(entry) ? entry : OTHER_VALUE)),
    otherText: value.find((entry) => !regularOptionValues.has(entry)) ?? '',
  };
}

export function MultipleChoiceField({
  id,
  field,
  value,
  disabled,
  onChange,
  renderOptionLabel = (label) => <RichTextPreview mode="trusted" as="span" value={label} />,
}: {
  id: string;
  field: FormField;
  value: string[];
  disabled: boolean;
  renderOptionLabel?: (label: string) => ReactNode;
  onChange: (val: string[]) => void;
}) {
  const initialState = multipleChoiceState(field, value);
  const optionsSignature = optionValueSignature(field);
  const previousOptionsSignature = useRef(optionsSignature);
  const presentation = formFieldPresentation(field);

  const [checkboxValues, setCheckboxValues] = useState(initialState.checkboxValues);
  const [otherText, setOtherText] = useState(initialState.otherText);

  const isOtherSelected = checkboxValues.includes(OTHER_VALUE);

  const allOptions = [
    ...(field.options ?? []),
    ...(field.addOther ? [{ label: field.otherLabel ?? 'Other', value: OTHER_VALUE }] : []),
  ];

  useEffect(() => {
    const optionsChanged = previousOptionsSignature.current !== optionsSignature;
    previousOptionsSignature.current = optionsSignature;

    const localValues = multipleChoiceParentValues(checkboxValues, otherText);
    if (!optionsChanged && equalStringArrays(value, localValues)) return;

    const nextState = multipleChoiceState(field, value);
    setCheckboxValues(nextState.checkboxValues);
    setOtherText(nextState.otherText);
  }, [checkboxValues, field, optionsSignature, otherText, value]);

  const handleCheckboxChange = (vals: string[]) => {
    setCheckboxValues(vals);
    onChange(multipleChoiceParentValues(vals, otherText));
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    onChange(multipleChoiceParentValues(checkboxValues, text));
  };

  const optionsWithDisabled = disabled
    ? allOptions.map((o) => ({ ...o, disabled: true }))
    : allOptions;

  return (
    <div className="grid gap-2">
      <CheckboxGroupInput
        id={id}
        label={field.label}
        name={field.key}
        value={checkboxValues}
        options={optionsWithDisabled}
        renderOptionLabel={renderOptionLabel}
        isRequired={field.required}
        showAsterisk={presentation.showAsterisk}
        helperText={presentation.helperText}
        helperTextPlacement="afterControl"
        onChange={handleCheckboxChange}
        layout={field.displayInTwoColumns ? 'grid' : 'vertical'}
      />
      {field.addOther && isOtherSelected && (
        <TextInput
          id={`${id}-other`}
          aria-label={`${field.label}: ${richTextToPlainText(field.otherLabel) || 'Other'}`}
          value={otherText}
          placeholder={field.otherPlaceholder ?? 'Add your own option'}
          onChange={(e) => handleOtherTextChange(e.target.value)}
          disabled={disabled}
          isRequired
          showAsterisk
          required
        />
      )}
    </div>
  );
}
